// QuestionRichField — campo WYSIWYG focado em edição de questões
// (enunciado, alternativa, caso, gabarito_modelo, etc).
// [[project-question-wysiwyg-editor]]
//
// Self-contained: zero dependências de apps específicos. Recebe value em
// markdown canônico, devolve markdown canônico no onChange. Toolbar pequena
// + paste image opcional via callback (caller decide se aceita e onde
// guarda — geralmente vai pro array `imagens[]` da questão).
//
// Por que não reusar o RichTextEditor do CadernoNotesPanel?
//   - Ele tem ~700 linhas acopladas com api/openPrompt/aiBubble do student-new
//   - Aqui o escopo é menor: sem AI bubble, sem callouts, sem templates
//   - Imagens/tabelas não ficam INLINE no campo — viram placeholders e o
//     caller gerencia o array externo. Diferente do Caderno onde a imagem
//     vira <img> dentro da nota
//
// Variantes:
//   variant="inline"  → 1 linha visual (alternativas), toolbar mínima
//   variant="block"   → multi-linha (enunciado/caso), toolbar média
//   variant="rich"    → multi-linha + headings/lists/blockquote (resolução)

import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Palette,
  Quote,
  Sigma,
  Strikethrough,
  Table as TableIcon,
  Underline
} from "lucide-react";
import { renderBlock as renderMdBlock, COLOR_TOKENS } from "../lib/markdown";
import { ensureKaTeX, renderMathIn } from "../lib/math";
import {
  expandPlaceholders,
  htmlToCanonicalMd,
  type MdToHtmlOpts
} from "../lib/question-md-html";
import { cn } from "../lib/utils";

// Paleta nomeada exposta no menu de cor (subset do markdown.ts pra UI enxuta)
const COLOR_PALETTE: Array<{ name: string; hex: string; label: string }> = [
  { name: "vermelho", hex: COLOR_TOKENS.vermelho, label: "Vermelho" },
  { name: "azul", hex: COLOR_TOKENS.azul, label: "Azul" },
  { name: "verde", hex: COLOR_TOKENS.verde, label: "Verde" },
  { name: "amarelo", hex: COLOR_TOKENS.amarelo, label: "Amarelo" },
  { name: "roxo", hex: COLOR_TOKENS.roxo, label: "Roxo" },
  { name: "laranja", hex: COLOR_TOKENS.laranja, label: "Laranja" },
  { name: "rosa", hex: COLOR_TOKENS.rosa, label: "Rosa" },
  { name: "cinza", hex: COLOR_TOKENS.cinza, label: "Cinza" }
];

export type QuestionRichVariant = "inline" | "block" | "rich";

export interface QuestionRichFieldProps {
  /** Markdown canônico (renderInline/renderBlock format). */
  value: string;
  /** Disparado em blur do contentEditable + ao apertar Ctrl+S. Recebe MD canônico. */
  onChange: (md: string) => void;
  variant?: QuestionRichVariant;
  placeholder?: string;
  /** Classes extras no wrapper. */
  className?: string;
  /** Quando informado, habilita botão "Imagem" e paste de imagem. Recebe
   *  dataUrl (base64) + alt + posição do caret no MD após inserção. Retorna
   *  o índice no array imagens[] da questão. Caller insere [img:N] no MD via
   *  o próximo render (passe value novo). */
  onPasteImage?: (params: { dataUrl: string; alt: string }) => Promise<number>;
  /** Modo simples (legado do VisualBankEditor): paste de imagem produz
   *  diretamente `![imagem](dataUrl)` inline no markdown, sem precisar
   *  manter array externo. Caller só recebe a notificação após inserir.
   *  Quando definido, NÃO usar `onPasteImage` (mutuamente exclusivo). */
  onPasteImageInline?: (dataUrl: string) => void;
  /** Disparado quando o usuário clica numa <img> dentro do editor.
   *  Caller pode abrir modal de crop. Recebe src, função `replace` que troca
   *  a URL no markdown, e função `remove` que apaga a imagem inline inteira
   *  (tag <img> + sintaxe `![](...)` correspondente). */
  onImageClick?: (params: {
    src: string;
    replace: (nextSrc: string) => void;
    remove: () => void;
  }) => void;
  /** Quando informado, habilita botão "Tabela". Caller abre seu modal. */
  onInsertTable?: () => Promise<number | null>;
  /** Renderizado abaixo do editor — útil pra strip de imagens/tabelas
   *  detectadas no conteúdo, controles de crop, empty state. */
  footerSlot?: preact.ComponentChildren;
  /** Resolve placeholders [img:N] e [tabela:N] no render. */
  resolveImage?: MdToHtmlOpts["resolveImage"];
  resolveTable?: MdToHtmlOpts["resolveTable"];
  /** Desabilita edição (read-only preview com mesma renderização). */
  readOnly?: boolean;
  /** Chama quando usuário aperta Enter em variant=inline (alternativas). */
  onEnterInline?: () => void;
  /** Disabled state visual. */
  disabled?: boolean;
}

const MAX_LINK_LEN = 500;

function looksLikeHtml(s: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(s);
}

function safeQueryState(cmd: string): boolean {
  try {
    return document.queryCommandState(cmd);
  } catch {
    return false;
  }
}

export function QuestionRichField({
  value,
  onChange,
  variant = "block",
  placeholder = "",
  className,
  onPasteImage,
  onPasteImageInline,
  onImageClick,
  onInsertTable,
  resolveImage,
  resolveTable,
  readOnly,
  onEnterInline,
  disabled,
  footerSlot
}: QuestionRichFieldProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Sentinela impossível de bater com markdown legítimo: força a 1ª execução
  // do useLayoutEffect a popular o DOM mesmo se `value` já chegou (ex.: aluno
  // abre uma questão e parent passa value sync — não dava pra distinguir
  // "1ª render" de "value já emitido" antes). [[project-question-wysiwyg-editor]]
  const lastEmittedMd = useRef<string>("\0__qrf_uninit__\0");
  // Timer pra debounce do emit em onInput (atualização real-time da prévia
  // sem custar uma conversão por keystroke). Cancelado em blur (que emite
  // imediatamente). 180ms é confortável: prévia parece "ao vivo" mas não
  // converte HTML→MD a cada letra.
  const emitDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [active, setActive] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    code: false
  });
  const [openMenu, setOpenMenu] = useState<null | "color" | "highlight">(null);

  // Inicializa innerHTML ao montar ou quando value muda externamente
  // (diferente do MD que acabamos de emitir).
  useLayoutEffect(() => {
    if (!ref.current) return;
    if (value === lastEmittedMd.current) return; // evita re-render durante digitação
    // Marca o valor atual como "renderizado" pra que o próximo blur que
    // emita o mesmo MD não dispare re-init do DOM (limpando seleção).
    lastEmittedMd.current = value;
    let html = "";
    if (value) {
      html = looksLikeHtml(value) ? value : renderMdBlock(value);
      html = expandPlaceholders(html, { resolveImage, resolveTable });
    }
    ref.current.innerHTML = html || "<p><br></p>";
    // Render math se houver
    if (html.includes("math-inline") || /\\\(|\\\[/.test(html)) {
      void ensureKaTeX().then(() => {
        if (ref.current) renderMathIn(ref.current);
      });
    }
  }, [value, resolveImage, resolveTable]);

  // styleWithCSS uma vez ao montar
  useEffect(() => {
    try {
      document.execCommand("styleWithCSS", false, "true");
    } catch {
      /* ignore */
    }
    // Cleanup do debounce ao desmontar pra não disparar onChange em
    // componente já zumbi (causa state stale).
    return () => {
      if (emitDebounceRef.current) {
        clearTimeout(emitDebounceRef.current);
        emitDebounceRef.current = null;
      }
    };
  }, []);

  // Track formato ativo na seleção pra ativar botões da toolbar
  useEffect(() => {
    function refresh() {
      const sel = window.getSelection();
      if (!sel || !sel.anchorNode || !ref.current) return;
      if (!ref.current.contains(sel.anchorNode)) return;
      setActive({
        bold: safeQueryState("bold"),
        italic: safeQueryState("italic"),
        underline: safeQueryState("underline"),
        strike: safeQueryState("strikeThrough"),
        code: false // execCommand não tem code; checamos via DOM ancestor
      });
    }
    document.addEventListener("selectionchange", refresh);
    return () => document.removeEventListener("selectionchange", refresh);
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    function onDown(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-qrf-menu]")) setOpenMenu(null);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openMenu]);

  function emit() {
    if (!ref.current) return;
    if (emitDebounceRef.current) {
      clearTimeout(emitDebounceRef.current);
      emitDebounceRef.current = null;
    }
    const md = htmlToCanonicalMd(ref.current.innerHTML);
    lastEmittedMd.current = md;
    onChange(md);
  }

  function emitDebounced() {
    if (emitDebounceRef.current) clearTimeout(emitDebounceRef.current);
    emitDebounceRef.current = setTimeout(() => {
      emitDebounceRef.current = null;
      emit();
    }, 180);
  }

  function exec(command: string, val?: string) {
    if (!ref.current) return;
    ref.current.focus();
    try {
      document.execCommand("styleWithCSS", false, "true");
    } catch {
      /* ignore */
    }
    try {
      document.execCommand(command, false, val);
    } catch {
      /* ignore */
    }
    // Emite após pequeno delay pra DOM commit
    setTimeout(emit, 0);
  }

  function applyColor(name: string) {
    setOpenMenu(null);
    if (!ref.current) return;
    const hex = COLOR_TOKENS[name];
    if (!hex) return;
    exec("foreColor", hex);
  }

  function applyHighlight(name: string) {
    setOpenMenu(null);
    if (!ref.current) return;
    if (name === "none") {
      exec("hiliteColor", "transparent");
      return;
    }
    const hex = COLOR_TOKENS[name];
    if (!hex) return;
    // Aplica um background leve usando hex + alpha
    const bg = hex + "33"; // ~20% alpha
    try {
      document.execCommand("styleWithCSS", false, "true");
      const ok = document.execCommand("hiliteColor", false, bg);
      if (!ok) document.execCommand("backColor", false, bg);
    } catch {
      /* ignore */
    }
    setTimeout(emit, 0);
  }

  async function insertLink() {
    if (!ref.current) return;
    ref.current.focus();
    const url = window.prompt("URL do link (https://…)");
    if (!url) return;
    const trimmed = url.trim();
    if (!trimmed || trimmed.length > MAX_LINK_LEN) return;
    const normalized = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    exec("createLink", normalized);
    // Marca target=_blank
    setTimeout(() => {
      if (!ref.current) return;
      ref.current.querySelectorAll('a[href]').forEach((a) => {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      });
      emit();
    }, 10);
  }

  async function insertMath() {
    if (!ref.current) return;
    const tex = window.prompt(
      "LaTeX (sem delimitadores). Ex: \\frac{1}{2}, x^2, \\sen x"
    );
    if (!tex) return;
    const trimmed = tex.trim();
    if (!trimmed) return;
    const html = `<span class="math-inline" data-tex="${escapeAttr(trimmed)}" contenteditable="false">\\(${escapeHtml(trimmed)}\\)</span>&nbsp;`;
    exec("insertHTML", html);
    // Renderiza
    void ensureKaTeX().then(() => {
      if (ref.current) renderMathIn(ref.current);
    });
  }

  async function pickImageFromDisk() {
    if (!onPasteImage) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = () => {
      const f = input.files?.[0];
      input.remove();
      if (f) void handleImageFile(f);
    };
    input.click();
  }

  async function handleImageFile(file: File) {
    if (!onPasteImage) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert("Imagem grande demais (limite 5 MB).");
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Falha ao ler imagem"));
      reader.readAsDataURL(file);
    }).catch(() => null);
    if (!dataUrl) return;
    const idx = await onPasteImage({
      dataUrl,
      alt: file.name.replace(/\.[^.]+$/, "")
    });
    // Insere placeholder no caret
    if (!ref.current) return;
    ref.current.focus();
    const placeholder = ` [img:${idx}] `;
    exec("insertText", placeholder);
  }

  function onPaste(e: ClipboardEvent) {
    if (!(onPasteImage || onPasteImageInline) || !e.clipboardData) return;
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.type && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (!f) continue;
        e.preventDefault();
        if (onPasteImageInline) {
          void handleImageFileInline(f);
        } else {
          void handleImageFile(f);
        }
        return;
      }
    }
  }

  async function handleImageFileInline(file: File) {
    if (!onPasteImageInline) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert("Imagem grande demais (limite 5 MB).");
      return;
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Falha ao ler imagem"));
      reader.readAsDataURL(file);
    }).catch(() => null);
    if (!dataUrl) return;
    onPasteImageInline(dataUrl);
  }

  function onKeyDown(e: KeyboardEvent) {
    // Ctrl+B/I/U continuam funcionando nativo. Adicionais:
    if (e.key === "Enter" && variant === "inline") {
      e.preventDefault();
      onEnterInline?.();
      emit();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      emit();
      return;
    }
  }

  function onBlur() {
    emit();
  }

  /** Disparado pelo contentEditable em CADA mutação do conteúdo (digitação,
   *  paste, exec command nativo do browser). Debounce 180ms pra prévia
   *  parecer ao-vivo sem custar 1 conversão por keystroke. Paste de imagem
   *  via clipboard nativo (sem onPasteImage callback) entra por aqui →
   *  prévia recebe o ![imagem](data:...) em < 200ms.
   *  [[project-question-wysiwyg-editor]] */
  function onInputChange() {
    emitDebounced();
  }

  function onClick(e: MouseEvent) {
    if (!onImageClick) return;
    const target = e.target as HTMLElement;
    if (!target || target.tagName !== "IMG") return;
    const img = target as HTMLImageElement;
    const src = img.getAttribute("src");
    if (!src) return;
    e.preventDefault();
    e.stopPropagation();
    onImageClick({
      src,
      replace: (nextSrc: string) => {
        if (!ref.current) return;
        // Substitui todas as ocorrências da src no HTML do editor → propaga
        // pro markdown via emit. Usa split/join porque dataURLs têm chars
        // regex-quebráveis ('+', '/', '=').
        ref.current.innerHTML = ref.current.innerHTML.split(src).join(nextSrc);
        emit();
      },
      remove: () => {
        if (!ref.current) return;
        // Remove TODAS as <img> com essa src (cobre o caso onde o user
        // colou a mesma imagem em mais de um lugar — comportamento
        // consistente com replace). [[bug-2026-05-28: imagens inline não saiam]]
        const imgs = Array.from(ref.current.querySelectorAll("img"));
        let removed = false;
        for (const node of imgs) {
          if (node.getAttribute("src") === src) {
            node.remove();
            removed = true;
          }
        }
        if (removed) emit();
      }
    });
  }

  async function handleInsertTable() {
    if (!onInsertTable) return;
    const idx = await onInsertTable();
    if (idx == null || !ref.current) return;
    ref.current.focus();
    const placeholder = `\n[tabela:${idx}]\n`;
    exec("insertText", placeholder);
  }

  // Toolbar simplificada por variante
  const showHeadings = variant === "rich";
  const showLists = variant === "rich" || variant === "block";
  const showBlockquote = variant === "rich";
  const showImageBtn = !!onPasteImage && variant !== "inline";
  const showTableBtn = !!onInsertTable && variant !== "inline";

  if (readOnly) {
    return (
      <div
        class={cn(
          "rounded-md border bg-card px-3 py-2 text-sm",
          className
        )}
        ref={ref}
      />
    );
  }

  return (
    <div
      class={cn(
        "rounded-md border bg-background",
        disabled && "opacity-60",
        className
      )}
    >
      <div class="flex flex-wrap items-center gap-0.5 border-b bg-muted/40 px-1 py-1 text-muted-foreground">
        <TbBtn
          label="Negrito (Ctrl+B)"
          active={active.bold}
          onClick={() => exec("bold")}
        >
          <Bold class="size-3.5" />
        </TbBtn>
        <TbBtn
          label="Itálico (Ctrl+I)"
          active={active.italic}
          onClick={() => exec("italic")}
        >
          <Italic class="size-3.5" />
        </TbBtn>
        <TbBtn
          label="Sublinhado (Ctrl+U)"
          active={active.underline}
          onClick={() => exec("underline")}
        >
          <Underline class="size-3.5" />
        </TbBtn>
        <TbBtn
          label="Tachado"
          active={active.strike}
          onClick={() => exec("strikeThrough")}
        >
          <Strikethrough class="size-3.5" />
        </TbBtn>

        <TbDivider />

        <div class="relative" data-qrf-menu>
          <TbBtn
            label="Cor do texto"
            active={openMenu === "color"}
            onClick={() => setOpenMenu(openMenu === "color" ? null : "color")}
          >
            <Palette class="size-3.5" />
          </TbBtn>
          {openMenu === "color" && (
            <ColorMenu onPick={applyColor} onClear={() => exec("foreColor", "inherit")} />
          )}
        </div>
        <div class="relative" data-qrf-menu>
          <TbBtn
            label="Marca-texto"
            active={openMenu === "highlight"}
            onClick={() =>
              setOpenMenu(openMenu === "highlight" ? null : "highlight")
            }
          >
            <Highlighter class="size-3.5" />
          </TbBtn>
          {openMenu === "highlight" && (
            <ColorMenu
              onPick={applyHighlight}
              onClear={() => applyHighlight("none")}
            />
          )}
        </div>

        <TbDivider />

        <TbBtn label="Código inline" onClick={() => wrapWithCode(ref, emit)}>
          <Code class="size-3.5" />
        </TbBtn>
        <TbBtn label="Fórmula LaTeX (inline)" onClick={insertMath}>
          <Sigma class="size-3.5" />
        </TbBtn>
        <TbBtn label="Link" onClick={insertLink}>
          <LinkIcon class="size-3.5" />
        </TbBtn>

        {showHeadings && (
          <>
            <TbDivider />
            <TbBtn label="Título 2" onClick={() => exec("formatBlock", "<h2>")}>
              <Heading2 class="size-3.5" />
            </TbBtn>
            <TbBtn label="Título 3" onClick={() => exec("formatBlock", "<h3>")}>
              <Heading3 class="size-3.5" />
            </TbBtn>
          </>
        )}
        {showLists && (
          <>
            <TbDivider />
            <TbBtn
              label="Lista com marcadores"
              onClick={() => exec("insertUnorderedList")}
            >
              <List class="size-3.5" />
            </TbBtn>
            <TbBtn
              label="Lista numerada"
              onClick={() => exec("insertOrderedList")}
            >
              <ListOrdered class="size-3.5" />
            </TbBtn>
          </>
        )}
        {showBlockquote && (
          <TbBtn
            label="Citação"
            onClick={() => exec("formatBlock", "<blockquote>")}
          >
            <Quote class="size-3.5" />
          </TbBtn>
        )}

        {(showImageBtn || showTableBtn) && <TbDivider />}
        {showImageBtn && (
          <TbBtn label="Inserir imagem" onClick={pickImageFromDisk}>
            <ImageIcon class="size-3.5" />
          </TbBtn>
        )}
        {showTableBtn && (
          <TbBtn label="Inserir tabela" onClick={handleInsertTable}>
            <TableIcon class="size-3.5" />
          </TbBtn>
        )}
      </div>

      <div
        ref={ref}
        contentEditable={!disabled}
        spellcheck
        onBlur={onBlur}
        onInput={onInputChange}
        onClick={onClick as unknown as preact.JSX.MouseEventHandler<HTMLDivElement>}
        onPaste={onPaste as unknown as preact.JSX.ClipboardEventHandler<HTMLDivElement>}
        onKeyDown={onKeyDown as unknown as preact.JSX.KeyboardEventHandler<HTMLDivElement>}
        data-placeholder={placeholder}
        class={cn(
          "qrf-editor px-3 py-2 text-sm leading-relaxed outline-none",
          variant === "inline" ? "min-h-[2rem]" : "min-h-[6rem]",
          "[&_p]:my-1 [&_h2]:mt-3 [&_h2]:text-lg [&_h2]:font-semibold",
          "[&_h3]:mt-2 [&_h3]:text-base [&_h3]:font-semibold",
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
          "[&_blockquote]:border-l-4 [&_blockquote]:border-violet-500/40 [&_blockquote]:bg-violet-500/5 [&_blockquote]:pl-3 [&_blockquote]:italic",
          "[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded [&_img]:border",
          "[&_a]:text-primary [&_a]:underline",
          "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em]",
          // Cursor pointer em imagens clicáveis pra indicar que dá pra cropar
          onImageClick && "[&_img]:cursor-pointer [&_img]:hover:opacity-80"
        )}
      />
      {footerSlot && <div class="border-t bg-muted/20">{footerSlot}</div>}
    </div>
  );
}

function TbBtn({
  label,
  active,
  onClick,
  children
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: preact.ComponentChildren;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      class={cn(
        "flex h-7 w-7 items-center justify-center rounded transition hover:bg-background hover:text-foreground",
        active && "bg-background text-violet-700 dark:text-violet-300"
      )}
    >
      {children}
    </button>
  );
}

function TbDivider() {
  return <div class="mx-0.5 h-5 w-px bg-border" />;
}

function ColorMenu({
  onPick,
  onClear
}: {
  onPick: (name: string) => void;
  onClear: () => void;
}) {
  return (
    <div
      data-qrf-menu
      class="absolute left-0 top-full z-20 mt-1 grid w-44 grid-cols-4 gap-1 rounded-md border bg-popover p-2 shadow-lg"
    >
      {COLOR_PALETTE.map((c) => (
        <button
          key={c.name}
          type="button"
          title={c.label}
          aria-label={c.label}
          onClick={() => onPick(c.name)}
          class="h-7 w-7 rounded border hover:scale-110"
          style={{ background: c.hex }}
        />
      ))}
      <button
        type="button"
        onClick={onClear}
        class="col-span-4 mt-1 rounded border bg-background py-1 text-[11px] hover:bg-muted"
      >
        Remover cor
      </button>
    </div>
  );
}

function wrapWithCode(
  ref: { current: HTMLDivElement | null },
  emit: () => void
) {
  if (!ref.current) return;
  ref.current.focus();
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed) return;
  const text = sel.toString();
  if (!text) return;
  try {
    document.execCommand(
      "insertHTML",
      false,
      `<code>${escapeHtml(text)}</code>`
    );
  } catch {
    /* ignore */
  }
  setTimeout(emit, 0);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function escapeAttr(s: string): string {
  return s.replace(/"/g, "&quot;");
}
